import {Controller} from "@hotwired/stimulus"
import {SlickDataView, SlickGrid, SlickResizer, SlickCellSelectionModel, SlickCellCopyManager} from "slickgrid"
import {Filter, Hash, Type, Calendar1, createIcons} from 'lucide'

// ════════════════════════════════════════════════════════════════════════════
// Large-dataset report grid (replaces the old ag-grid `grid` controller).
//
// Modeled deliberately on the PROVEN data_rows_controller.js setup — the exact
// SlickGrid options that render correctly in this app: explicitInitialization +
// init(), forceFitColumns:true, a real SlickDataView (NOT a sparse array), and
// `element.style.width = '100%'`. On top of that it adds the three things the
// large report needs that data-rows doesn't:
//   1. paged / infinite loading  — fetch the next 100 rows as the user scrolls
//   2. a footer totals row        — createFooterRow + getFooterRowColumn
//   3. header filter funnels      — link to the column-filter side modal
//
// Server data shape (Pivot::LargeData):
//   headers : ag column defs (headerName / type / colId / sortable / children)
//   rows    : array of rows, each an array of positional cells; a cell is
//             { value, display_value, html_style, number_scale, comma_style,
//               precision, show_drilldown_link, drilldown_anchor_tag }
//   totals  : [ positionalTotalCells ]  (same cell shape, aligned to columns)
// We adapt this to SlickGrid positionally — column K ↔ cell K — keying each row
// as { id, id_1: html, id_1_style: cls, id_2: ... }.
// ════════════════════════════════════════════════════════════════════════════

// ── Currency cell formatting (self-contained copy of the helpers in
// slick_grid_controller.js; kept local so this controller has no coupling to
// the tree-path grid). Numeric cells ship as a compact sentinel placeholder
//   \u0001$ amount \u0001 number_scale \u0001 comma_style \u0001 precision \u0001
// expanded + formatted once per render (mirrors currency_controller.js). ──
const CUR_RE = /\u0001\$([^\u0001]*)\u0001([^\u0001]*)\u0001([^\u0001]*)\u0001([^\u0001]*)\u0001/g
const CUR_SCALE_DIVISORS = {units: 1, thousands: 1000, lakhs: 100000, millions: 1000000, crores: 10000000}

let _curMeta = null
function curMeta() {
    if (_curMeta) return _curMeta
    const m = (n) => document.querySelector(`meta[name="${n}"]`)?.content
    _curMeta = {
        mono: m('numericFontMonospace') === 'true',
        round: m('userRoundPreference') || 'units',
        comma: m('userCommaStyle') || 'indian',
        precision: m('userPrecision') || '',
        zeroAs: m('displayZeroAs') || ''
    }
    return _curMeta
}

const _intlCache = new Map()
function curIntl(locale, minFractionDigits) {
    const key = `${locale}|${minFractionDigits}`
    let f = _intlCache.get(key)
    if (!f) {
        f = new Intl.NumberFormat(locale, {minimumFractionDigits: minFractionDigits})
        _intlCache.set(key, f)
    }
    return f
}

const isBlankAttr = (v) => v == null || v === '' || v === 'null' || v === 'undefined'

function buildCurrencyHtml(amountStr, ns, cs, pr, scaleOverride) {
    const meta = curMeta()
    const amt = parseFloat(amountStr)

    let scale = scaleOverride
    if (isBlankAttr(scale)) scale = ns
    if (isBlankAttr(scale)) scale = meta.round

    let precStr = scaleOverride ? '' : pr
    if (isBlankAttr(precStr)) precStr = meta.precision
    const precision = (precStr === '') ? (scale === 'units' ? 0 : 2) : parseInt(precStr)

    const commaStyle = isBlankAttr(cs) ? meta.comma : cs
    const locale = commaStyle === 'western' ? 'en-US' : 'en-IN'

    let text
    if (amt === 0 || isNaN(amt)) {
        text = meta.zeroAs === 'zero' ? '0' : '-'
    } else {
        const divisor = CUR_SCALE_DIVISORS[scale] || 1
        const rounded = parseFloat((Math.abs(amt) / divisor).toFixed(precision))
        const formatted = curIntl(locale, precision).format(rounded)
        text = amt < 0 ? `(${formatted})` : formatted
    }

    let cls = 'currency'
    if (amt < 0) cls += ' text-red-600'
    if (meta.mono) cls += ' jetbrains-mono'
    return `<span class="${cls}" data-currency-amount-value="${amountStr}" data-currency-number-scale-value="${ns}" data-currency-comma-style-value="${cs}" data-currency-precision-value="${pr}">${text}</span>`
}

function renderCellValue(value, scaleOf) {
    if (typeof value !== 'string') return value == null ? '' : value
    if (value.indexOf('\u0001$') === -1) return value
    return value.replace(CUR_RE, (_m, amount, ns, cs, pr) => buildCurrencyHtml(amount, ns, cs, pr, scaleOf()))
}

function escapeHtml(s) {
    return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

export default class extends Controller {
    static targets = ['element', 'rowCount', 'loader']

    static values = {
        url: {type: String, default: ''},
        skey: {type: String, default: ''},
        totalCount: {type: Number, default: 0},
        pageSize: {type: Number, default: 100},
        headers: {type: String, default: ''},        // window-var id holding ag headers JSON
        ogHeaders: {type: Array, default: []},        // fallback inline headers
        fontSize: {type: String, default: ''},
        containerName: {type: String, default: ''},
        filterUrl: {type: String, default: ''},
        filteredColIds: {type: Array, default: []},
        reportId: {type: String, default: ''},        // @child.id — scopes apply-filter events
        minHeight: {type: Number, default: 650},
        forceFit: {type: Boolean, default: true},
        frozenLeft: {type: Number, default: 0}
    }

    connect() {
        this.loading = false
        this.loadedCount = 0
        // `exhausted` is the authoritative "no more pages" latch. We do NOT rely
        // on totalCount alone: a filter can drop the count to 0, and the old
        // `totalCount > 0` guards short-circuit to false at 0 — which re-opened
        // the fetch loop and hammered the server. Once a short/empty page comes
        // back we latch this true and never fetch again until a re-sort.
        this.exhausted = false
        this.sortParam = null
        // Column filters are applied server-side by reloading this whole report
        // block (see _column_filter.html.erb): Apply submits a GET to `page`
        // targeting the lz<id>_table frame, which re-renders the grid with the
        // filter baked into the page URL. So the large_data url this grid pages
        // against already carries filters[…] — infinite scroll and sort stay
        // filtered with no client-side filter state to track here.
        this.footerCells = null
        this.hiddenIds = new Set()
        this.initGrid()
    }

    disconnect() {
        if (this.scaleObserver) this.scaleObserver.disconnect()
        if (this.scrollTimer) clearInterval(this.scrollTimer)
        if (this._iconRaf) cancelAnimationFrame(this._iconRaf)
        if (this._wheelEl && this._wheelHandler) this._wheelEl.removeEventListener('wheel', this._wheelHandler)
        if (this.grid) this.grid.destroy()
    }

    setTotalCount(n) {
        if (n == null) return
        this.totalCountValue = Number(n)
        if (this.hasRowCountTarget) this.rowCountTarget.textContent = `${this.totalCountValue.toLocaleString()} rows`
    }

    // ── grid bootstrap (structure mirrors data_rows_controller.js) ──────────────
    initGrid() {
        this.elementTarget.innerHTML = ''
        this.elementTarget.style.width = '100%'

        const card = this.element.querySelector('.lz-report-card') || this.element
        this.card = card
        this.scaleOf = () => {
            const s = card.getAttribute('data-report-scale')
            return (s && s !== '' && s !== 'default') ? s : null
        }

        let rowHeight = 32
        if (this.fontSizeValue === 'medium') rowHeight = 35
        else if (this.fontSizeValue === 'large') rowHeight = 39
        this.rowHeight = rowHeight

        this.allColumns = this.buildColumns()

        const options = {
            enableColumnReorder: false,
            autoHeight: false,
            enableTextSelectionOnCells: true,
            enableCellNavigation: true,
            explicitInitialization: true,
            rowHeight: rowHeight,
            frozenColumn: this.frozenLeftValue >= 0 ? this.frozenLeftValue : -1,
            createFooterRow: true,
            showFooterRow: true,
            footerRowHeight: rowHeight,
            emulatePagingWhenScrolling: false,
            // Off: columns are sized to fit their header + data (autosizeColumns)
            // and wide tables scroll horizontally. Force-fit would cram every
            // column into the viewport width (truncating headers/data) and strip
            // the last column's resize handle.
            forceFitColumns: false,
            // Use NATIVE (pixel-precise, momentum-aware) scrolling for the wheel
            // and touchpad. SlickGrid's built-in handler does
            //   scrollTop -= (wheelDeltaY / 120) * rowHeight
            // which maps a mouse notch (wheelDeltaY 120) to exactly one row, but
            // quantizes a precision touchpad's fine deltas into coarse row jumps
            // and strips momentum — so touchpad scrolling felt different from
            // dragging the scrollbar (which never hits this handler). Disabling it
            // routes wheel/touchpad through native scroll; the frozen pane still
            // syncs via SlickGrid's native `scroll` listener (handleScroll).
            enableMouseWheelScrollHandler: false
        }

        this.dataView = new SlickDataView()
        this.dataView.onRowCountChanged.subscribe(() => {
            this.grid.updateRowCount()
            this.grid.render()
        })
        this.dataView.onRowsChanged.subscribe((e, args) => {
            this.grid.invalidateRows(args.rows)
            this.grid.render()
        })

        this.grid = new SlickGrid(this.elementTarget, this.dataView, this.visibleColumns(), options)
        // See slick_grid_controller.js — avoid CodeMirror selectionchange crash.
        this.grid.getTextSelection = () => null
        this.grid.setSelectionModel(new SlickCellSelectionModel())
        this.registerCopy()

        // seed page 0 (rendered server-side, no fetch on first paint)
        const seed = this.convertRows(this.readSeedRows(), 0)
        this.loadedCount = seed.length
        // The server renders the FIRST page (≤ MAX_ROWS = pageSize) into the seed.
        // A short seed (incl. an empty filtered result) therefore IS the whole
        // dataset — mark exhausted so we never hit the server. This is what kills
        // the post-filter loop: a filter that returns 0 rows seeds 0 rows here.
        if (seed.length < this.pageSizeValue) this.exhausted = true
        this.dataView.beginUpdate()
        this.dataView.setItems(seed)
        this.dataView.endUpdate()

        this.resizer = new SlickResizer({
            container: this.containerSelector(),
            rightPadding: 5, bottomPadding: 10,
            minHeight: this.minHeightValue, minWidth: 250
        })
        this.grid.registerPlugin(this.resizer)

        // server-side sort: reset + reload from offset 0
        this.grid.onSort.subscribe((e, args) => {
            if (e.target && e.target.closest && e.target.closest('a')) return  // ignore funnel clicks
            this.applySort(args)
        })

        // infinite scroll: pull the next page as the viewport nears the loaded tail
        this.grid.onViewportChanged.subscribe(() => this.maybeLoadMore())
        this.grid.onColumnsResized.subscribe(() => this.renderFooter())

        // re-render visible cells + footer when the number-scale dropdown changes
        this.scaleObserver = new MutationObserver(() => {
            this.grid.invalidateAllRows()
            this.grid.render()
            this.renderFooter()
        })
        this.scaleObserver.observe(card, {attributes: true, attributeFilter: ['data-report-scale']})

        this.grid.init()
        this.autosizeColumns()
        this.bindWheelScroll()
        this.resizeGrid()
        this.renderFooter(this.readSeedTotals())
        this.renderHeaderIcons()
        this.maybeLoadMore()
    }

    // Pixel-accurate wheel/touchpad scrolling.
    //
    // The viewport (.slick-viewport-right) now shows a native vertical scrollbar
    // (CSS re-enables overflow-y:auto for this grid), but SlickGrid's built-in
    // wheel handler quantized into row jumps (deltaY/120 * rowHeight) which made
    // precision touchpads feel coarse and momentum-less. We disabled that handler
    // (enableMouseWheelScrollHandler:false) and drive the scroll ourselves with the
    // raw pixel deltas. Setting scrollTop fires SlickGrid's own `scroll` listener
    // (handleScroll) which re-renders and syncs the frozen pane — same path the
    // native scrollbar drag uses, so wheel/touchpad feels identical to dragging it.
    bindWheelScroll() {
        if (!this.grid) return
        const sc = this.scrollViewport()
        if (!sc) return
        this._wheelEl = this.elementTarget
        this._wheelHandler = (e) => {
            // honor wheel units: 0=pixels (touchpad / Chrome mouse), 1=lines, 2=pages
            const unit = e.deltaMode === 1 ? this.rowHeight : (e.deltaMode === 2 ? sc.clientHeight : 1)
            if (e.deltaY) {
                sc.scrollTop += e.deltaY * unit
                if (e.deltaX) sc.scrollLeft += e.deltaX * unit
                e.preventDefault()   // we own the wheel; preventDefault avoids double-scroll vs native overflow
            }
            // pure-horizontal gestures fall through to native overflow-x scrolling
        }
        this._wheelEl.addEventListener('wheel', this._wheelHandler, {passive: false})
    }

    // The resolved vertical scroll container: the RIGHT pane when columns are
    // frozen (the left pane is the pinned column), else the single LEFT pane.
    scrollViewport() {
        const frozen = this.grid && typeof this.grid.hasFrozenColumns === 'function' && this.grid.hasFrozenColumns()
        return this.elementTarget.querySelector(frozen ? '.slick-viewport-top.slick-viewport-right' : '.slick-viewport-top.slick-viewport-left')
    }

    // Fit each column to its content (header label + the seeded first page of
    // data), so columns reflect their data size instead of a fixed 140/175.
    // Columns with a configured width (col._explicitWidth) are left untouched.
    // With forceFitColumns off (the default now) these widths are used as-is and
    // wide tables scroll horizontally; a report that opts into force-fit uses
    // them as the base proportion before stretching to fill.
    autosizeColumns() {
        if (!this.grid) return
        const seed = this.readSeedRows()
        const ctx = (this._measureCanvas = this._measureCanvas || document.createElement('canvas')).getContext('2d')
        // Body font straight from a rendered cell so measureText matches reality.
        const bodyEl = this.elementTarget.querySelector('.slick-cell')
        const bcs = bodyEl ? getComputedStyle(bodyEl) : null
        const bodyFont = bcs ? `${bcs.fontWeight} ${bcs.fontSize} ${bcs.fontFamily}` : '400 13px sans-serif'
        const CELL_PAD = 22                          // cell left+right padding + slack
        const sampleN = Math.min(seed.length, 200)
        let changed = false

        this.allColumns.forEach((col) => {
            if (col._explicitWidth) return
            const k = parseInt(String(col.id).replace('id_', ''), 10)

            // ── header width: measure the REAL rendered label node. Its scrollWidth
            // is the full text width with uppercase + letter-spacing + font already
            // applied; (header.offsetWidth - label.offsetWidth) is the surrounding
            // chrome (type icon + filter funnel + paddings). This is exact and is
            // why headers no longer truncate.
            let headerW = 0
            const hnode = (typeof this.grid.getHeaderColumn === 'function') ? this.grid.getHeaderColumn(col.id) : null
            const label = hnode && hnode.querySelector('span[title]')
            if (label) {
                const chrome = Math.max(0, hnode.offsetWidth - label.offsetWidth)
                headerW = Math.ceil(label.scrollWidth) + chrome + 12   // +slack for sort indicator
            }

            // ── data width: widest cell across the seeded first page
            ctx.font = bodyFont
            let bodyW = 0
            for (let i = 0; i < sampleN; i++) {
                const t = this.cellMeasureText((seed[i] || [])[k - 1])
                if (!t) continue
                const cw = Math.ceil(ctx.measureText(t).width) + CELL_PAD
                if (cw > bodyW) bodyW = cw
            }

            const want = Math.max(headerW, bodyW, col.minWidth || 80)
            const next = Math.min(want, col.maxWidth || 600)
            if (next && next !== col.width) { col.width = next; changed = true }
        })
        if (changed) this.grid.setColumns(this.visibleColumns())
    }

    // Plain display text of a cell for width measurement (no DOM): formatted
    // currency for numerics, stripped anchor text for drilldowns, else the value.
    cellMeasureText(cell) {
        if (!cell) return ''
        if (typeof cell.value === 'number') {
            const ns = cell.number_scale == null ? '' : cell.number_scale
            const cs = cell.comma_style == null ? '' : cell.comma_style
            const pr = cell.precision == null ? '' : cell.precision
            return buildCurrencyHtml(String(cell.value), ns, cs, pr, this.scaleOf()).replace(/<[^>]*>/g, '')
        }
        if (cell.show_drilldown_link && cell.drilldown_anchor_tag) {
            return String(cell.drilldown_anchor_tag).replace(/<[^>]*>/g, ' ').trim()
        }
        const dv = (cell.display_value != null) ? cell.display_value : cell.value
        return dv == null ? '' : String(dv)
    }

    containerSelector() {
        return this.containerNameValue ? `.${this.containerNameValue}` : '.slick-main-container'
    }

    resizeGrid() {
        if (!this.resizer) return
        const h = this.minHeightValue || 650
        this.resizer.resizeGrid(0, {height: h, minHeight: h})
    }

    // ── columns ─────────────────────────────────────────────────────────────
    buildColumns() {
        const ag = (this.headersValue && window[this.headersValue]) || this.ogHeadersValue || []
        const cols = []
        let k = 0
        ag.forEach(h => {
            const children = h && h.children
            if (Array.isArray(children) && children.length) {
                children.forEach(ch => { k++; cols.push(this.leafColumn(ch, k, h.headerName)) })
            } else {
                k++; cols.push(this.leafColumn(h, k, null))
            }
        })
        return cols
    }

    leafColumn(h, k, group) {
        const numeric = !!(h && h.type === 'numericColumn')
        const field = 'id_' + k
        const colId = (h && h.colId) ? h.colId : null
        const name = (h && h.headerName != null) ? String(h.headerName) : ''
        // Show the filter funnel ONLY where ag-grid did in prod: the server sets
        // `filter: true` on a column def (generate_ag_hash) just for String-type
        // columns (and any explicit ag_grid_header override) — not numeric/date
        // aggregates. Gate on that flag so the funnel isn't shown on every column.
        const filterable = !!colId && !group && !!(h && h.filter)
        // Respect a server/ag_grid_header-configured width when present; otherwise
        // autosizeColumns() will fit it to the data (see initGrid). Configured
        // widths are flagged so autosize leaves them alone.
        const cfgWidth = (h && Number.isFinite(h.width)) ? h.width : null
        const col = {
            id: field,
            field: field,
            name: this.headerHtml(name, colId, filterable, numeric),
            dValue: name,                       // plain label for copy/header-row
            sortable: !!(h && h.sortable),
            resizable: !(h && h.resizable === false),   // default true (cols stay drag-resizable)
            filterColId: colId,
            filterable: filterable,
            numeric: numeric,
            // Seed width; autosizeColumns() refines non-configured columns to content.
            width: cfgWidth != null ? cfgWidth : (numeric ? 140 : 175),
            // Filterable columns render an extra funnel icon next to the type icon
            // + label, so they need a larger floor or the header gets crushed when
            // resized down. Respect a server minWidth if given.
            minWidth: (h && Number.isFinite(h.minWidth)) ? h.minWidth : (filterable ? 120 : 80),
            _explicitWidth: cfgWidth != null
        }
        if (h && Number.isFinite(h.maxWidth)) col.maxWidth = h.maxWidth
        if (numeric) {
            col.cssClass = 'text-right'
            col.headerCssClass = 'slick-header-column-right'
        }
        col.formatter = (row, cell, value, columnDef, dataContext) => {
            if (dataContext == null) return ''
            const html = renderCellValue(value, this.scaleOf)
            return {html: (html == null ? '' : html), addClasses: dataContext[field + '_style'] || ''}
        }
        return col
    }

    headerHtml(name, colId, filterable, numeric) {
        const icon = numeric ? 'hash' : 'type'
        const nameCls = 'truncate flex-1 px-1' + (numeric ? ' text-right' : '')
        let funnel = ''
        if (filterable && this.filterUrlValue) {
            const active = (this.filteredColIdsValue || []).includes(colId)
            const sep = this.filterUrlValue.includes('?') ? '&' : '?'
            const href = this.filterUrlValue + sep + 'column_name=' + encodeURIComponent(colId)
            const cls = active ? 'text-indigo-600 fill-indigo-600' : 'text-gray-400 hover:text-gray-600'
            // turbo-frame="_top" is REQUIRED: this grid lives inside the report
            // turbo-frame (lz<id>_table). Without it Turbo navigates that frame on
            // click — reloading the whole table with its loader. Targeting _top +
            // data-turbo-stream issues a top-level stream request that only updates
            // the modal frame (mirrors the "Show filters" link pattern).
            funnel = `<a href="${href}" data-turbo-stream="get" data-turbo-frame="_top" class="lz-grid-filter flex items-center justify-center px-1.5 h-full border-l border-gray-200 hover:bg-gray-100 transition-colors"><i data-lucide="filter" class="w-3 h-3 ${cls}"></i></a>`
        }
        return `<div class="flex items-center w-full h-full text-xs">
                  <span class="flex items-center min-w-0 flex-1 ${numeric ? 'justify-end' : ''}">
                    <i data-lucide="${icon}" class="mr-1 flex-shrink-0 w-3 h-3 text-gray-400"></i>
                    <span class="${nameCls}" title="${escapeHtml(name)}">${escapeHtml(name)}</span>
                  </span>
                  ${funnel}
                </div>`
    }

    visibleColumns() {
        return this.allColumns.filter(c => !this.hiddenIds.has(c.id))
    }

    renderHeaderIcons() {
        // Coalesce into a single rAF: fetchNextPage calls this on every page, and
        // during fast scrolling stacked setTimeout(50) callbacks fired mid-scroll,
        // each re-scanning the whole grid DOM. One scan per frame is enough — it
        // upgrades the header funnels and any row drilldown <i data-lucide> at once.
        if (this._iconRaf) return
        this._iconRaf = requestAnimationFrame(() => {
            this._iconRaf = null
            createIcons({
                icons: {Filter, Hash, Type, Calendar1},
                root: this.elementTarget,
                attrs: {width: 12, height: 12}
            })
        })
    }

    // ── row adaptation (positional cells → field-keyed hash) ─────────────────
    convertRows(cellArrays, startIndex) {
        const out = []
        ;(cellArrays || []).forEach((cells, i) => {
            const r = {id: 'r' + (startIndex + i)}
            ;(cells || []).forEach((cell, k) => {
                const f = 'id_' + (k + 1)
                r[f] = this.cellHtml(cell)
                r[f + '_style'] = this.cellStyle(cell)
            })
            out.push(r)
        })
        return out
    }

    cellHtml(cell) {
        if (!cell) return ''
        if (cell.show_drilldown_link && cell.drilldown_anchor_tag) return cell.drilldown_anchor_tag
        if (typeof cell.value === 'number') return this.currencyPlaceholder(cell)
        const dv = (cell.display_value != null) ? cell.display_value : cell.value
        return (dv == null) ? '' : String(dv)
    }

    currencyPlaceholder(cell) {
        const ns = cell.number_scale == null ? '' : cell.number_scale
        const cs = cell.comma_style == null ? '' : cell.comma_style
        const pr = cell.precision == null ? '' : cell.precision
        return `\x01$${cell.value}\x01${ns}\x01${cs}\x01${pr}\x01`
    }

    cellStyle(cell) {
        const s = (cell && cell.html_style) ? String(cell.html_style) : ''
        return s.replace(/\b(?:bg-white|md:sticky|md:left-0)\b/g, '').trim()
    }

    readSeedRows() {
        const el = this.element.querySelector('script[type="application/json"][data-slick-grid-rows]')
        try { return el ? JSON.parse(el.textContent) : [] } catch (e) { return [] }
    }

    readSeedTotals() {
        const el = this.element.querySelector('script[type="application/json"][data-slick-grid-totals]')
        try { return el ? JSON.parse(el.textContent) : null } catch (e) { return null }
    }

    // ── infinite / paged loading ──────────────────────────────────────────────
    maybeLoadMore() {
        if (this.loading || this.exhausted) return
        if (this.totalCountValue > 0 && this.loadedCount >= this.totalCountValue) return
        const vp = this.grid.getViewport()
        if (vp.bottom >= this.loadedCount - 30) this.fetchNextPage()
    }

    fetchNextPage() {
        if (this.loading || this.exhausted) return
        if (this.totalCountValue > 0 && this.loadedCount >= this.totalCountValue) return
        if (!this.urlValue) return
        this.loading = true
        this.showLoader()
        const offset = this.loadedCount
        fetch(this.pageUrl(offset), {headers: {'Accept': 'application/json'}})
            .then(r => r.json())
            .then(result => {
                const rows = this.convertRows(result.rows || [], offset)
                if (rows.length) {
                    this.dataView.beginUpdate()
                    rows.forEach(r => {
                        if (!this.dataView.getItemById(r.id)) this.dataView.addItem(r)
                    })
                    this.dataView.endUpdate()
                    this.loadedCount += rows.length
                    // A short page is the last page (server caps at pageSize).
                    if (rows.length < this.pageSizeValue) this.exhausted = true
                } else {
                    // No rows → end of the (possibly filtered) data. Latch
                    // exhausted; do NOT zero totalCount — that reopened the loop.
                    this.exhausted = true
                }
                // total_count is recomputed only on the offset-0 fetch (the
                // server's `with_total_rows` is offset-0 only) — refresh the
                // grid's bound count + the toolbar "N rows" display from it.
                if (offset === 0) {
                    if (result.total_count != null) this.setTotalCount(result.total_count)
                    if (result.totals) this.setFooter(result.totals)
                }
                this.loading = false
                // No explicit grid.render() here: appending rows above already
                // rendered twice via the DataView subscriptions (onRowCountChanged
                // + onRowsChanged). A third render re-ran the currency formatter on
                // every visible cell for nothing — the main per-page scroll hiccup.
                this.renderHeaderIcons()
                this.maybeLoadMore()
                // maybeLoadMore() may have kicked off the next page (loading=true
                // again) — only hide the indicator once nothing is in flight.
                if (!this.loading) this.hideLoader()
            })
            .catch(() => { this.loading = false; this.exhausted = true; this.hideLoader() })
    }

    showLoader() {
        if (this.hasLoaderTarget) this.loaderTarget.classList.remove('hidden')
    }

    hideLoader() {
        if (this.hasLoaderTarget) this.loaderTarget.classList.add('hidden')
    }

    pageUrl(offset) {
        // urlValue is the large_data endpoint rendered by the server for the
        // current view — it already carries any active filters[…] (the block was
        // reloaded with them), so paging just appends offset/skey/sort.
        let u = this.urlValue
        const sep = u.includes('?') ? '&' : '?'
        u += `${sep}offset=${offset}`
        if (this.skeyValue) u += `&skey=${encodeURIComponent(this.skeyValue)}`
        if (this.sortParam) u += `&ag_sort=${encodeURIComponent(this.sortParam)}`
        return u
    }

    // ── sort (server-side) ────────────────────────────────────────────────────
    applySort(args) {
        const col = args.sortCol || {}
        const colId = col.filterColId || col.id
        if (!colId) return
        this.sortParam = btoa(JSON.stringify([{colId: colId, sort: args.sortAsc ? 'asc' : 'desc'}]))
        this.loadedCount = 0
        this.exhausted = false
        this.loading = false
        this.footerCells = null
        this.dataView.beginUpdate()
        this.dataView.setItems([])
        this.dataView.endUpdate()
        this.grid.scrollRowToTop(0)
        this.fetchNextPage()
    }

    // ── footer totals ─────────────────────────────────────────────────────────
    setFooter(totals) {
        this.footerCells = (totals && totals[0]) ? totals[0] : null
        this.renderFooter()
    }

    renderFooter(totals) {
        if (totals !== undefined) { this.setFooter(totals); return }
        if (!this.grid) return
        const cells = this.footerCells
        this.grid.getColumns().forEach((col) => {
            let node
            try { node = this.grid.getFooterRowColumn(col.id) } catch (e) { node = null }
            if (!node) return
            const k = parseInt(String(col.id).replace('id_', ''), 10)
            const html = this.footerCellHtml(cells ? cells[k - 1] : null)
            // Write a value for EVERY column — text / no-total columns get an empty
            // string rather than being skipped, so the footer stays one continuous
            // strip with no missing (apparently "hidden") cells.
            node.innerHTML = (html === '') ? '' : renderCellValue(html, this.scaleOf)
            // Only ADD our styling — never overwrite className. SlickGrid puts the
            // cell's absolute-position classes (l{i}/r{i}) and the `frozen` class on
            // this node; clobbering them collapses the footer cells onto each other.
            node.classList.add('slick-footerrow-column', 'font-semibold')
            node.classList.toggle('text-right', !!col.numeric)
        })
    }

    // Footer cell → html. Returns '' for blank/missing cells so the totals row
    // shows clean empty cells instead of placeholders.
    footerCellHtml(cell) {
        if (!cell) return ''
        if (typeof cell.value === 'number') return this.currencyPlaceholder(cell)
        const dv = (cell.display_value != null) ? cell.display_value : cell.value
        return (dv == null || dv === '') ? '' : String(dv)
    }

    // ── copy (Ctrl/Cmd+C) ─────────────────────────────────────────────────────
    registerCopy() {
        const copyManager = new SlickCellCopyManager()
        this.grid.registerPlugin(copyManager)
        copyManager.onCopyCells.subscribe((e, args) => {
            const ranges = args.ranges
            if (!ranges || !ranges.length) return
            const rows = []
            for (const range of ranges) {
                for (let r = range.fromRow; r <= range.toRow; r++) {
                    const vals = []
                    for (let c = range.fromCell; c <= range.toCell; c++) {
                        const node = this.grid.getCellNode(r, c)
                        vals.push(node ? node.textContent.trim() : '')
                    }
                    rows.push(vals.join('\t'))
                }
            }
            navigator.clipboard.writeText(rows.join('\n')).catch(() => {})
            this.elementTarget.querySelectorAll('.slick-cell.copied').forEach(cell => {
                cell.classList.add('slick-cell-copy-flash')
                setTimeout(() => { cell.classList.remove('slick-cell-copy-flash', 'copied') }, 600)
            })
        })
    }

    // ── toolbar: show/hide columns (Change Columns) ───────────────────────────
    toggleColumn(event) {
        const name = event.currentTarget.getAttribute('data-grid-col-value')
        const col = this.allColumns.find(c => c.dValue === name)
        if (!col) return
        if (this.hiddenIds.has(col.id)) this.hiddenIds.delete(col.id)
        else this.hiddenIds.add(col.id)
        this.grid.setColumns(this.visibleColumns())
        this.renderFooter()
        this.renderHeaderIcons()
    }

    // ── toolbar: horizontal scroll buttons ────────────────────────────────────
    startScrollLeft() { this.startScroll(-25) }
    startScrollRight() { this.startScroll(25) }
    stopScrollLeft() { this.stopScroll() }
    stopScrollRight() { this.stopScroll() }

    startScroll(delta) {
        this.stopScroll()
        // Use the resolved scroll container (the RIGHT pane when frozen) so the
        // toolbar arrows scroll the data, not the pinned-column pane.
        const vp = this.scrollViewport() || this.elementTarget.querySelector('.slick-viewport')
        if (!vp) return
        this.scrollTimer = setInterval(() => { vp.scrollLeft += delta }, 16)
    }

    stopScroll() {
        if (this.scrollTimer) { clearInterval(this.scrollTimer); this.scrollTimer = null }
    }
}
